import React, { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Project, Measure, PlannedInvestment } from "@/types";
import { CategoryType } from "@/types/category";
import { getCategoryImageUrl } from "@/utils/getCategoryImageUrl";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { formatDateLong } from "@/utils/dateUtils";
import getProjectTranslation from "@/utils/getProjectTranslation";

interface UnifiedMeasure {
  id: number;
  project: Project;
  type: "implemented" | "planned";
  title: string;
  description: string;
  date?: string;
  category?: CategoryType;
  totalNet?: number;
  totalGross?: number;
  plannedBudget?: number;
  invoiceDate?: Date;
  actionDate?: string;
  forestLocation?: string;
  invoiceNumber?: string;
  batchId?: string;
  batchTitle?: string;
}

interface CategoryDetailMobilePageProps {
  category: CategoryType;
  projects: Project[];
  categoryData: Array<{
    category: CategoryType;
    displayName: string;
    description: string;
    totalMeasuresCount: number;
    color: string;
  }>;
  projectsMeasurements: Record<
    string,
    {
      planned: PlannedInvestment[];
      documented: Measure[];
    }
  >;
  onClose: () => void;
  customerColor: string;
}

const CategoryDetailMobilePage: React.FC<CategoryDetailMobilePageProps> = ({
  category,
  projects,
  categoryData,
  projectsMeasurements,
  onClose,
  customerColor,
}) => {
  const { t, language } = useLanguage();
  const [activeTab, setActiveTab] = useState("implemented");
  const [showAllImplemented, setShowAllImplemented] = useState(false);
  const [showAllPlanned, setShowAllPlanned] = useState(false);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = "hidden";

    return () => {
      // Restore body scroll when modal is closed
      document.body.style.overflow = "unset";
    };
  }, []);

  // Get category info
  const catInfo = categoryData.find((cat) => cat.category === category);
  const catImage = getCategoryImageUrl(category);

  // Get all measures for this category
  const allMeasures = projects.reduce((acc, project) => {
    const measurements = projectsMeasurements[project.id];
    if (!measurements) return acc;

    const { planned, documented } = measurements;

    // Add documented measures
    if (documented && documented.length > 0) {
      const categoryMeasures = documented.filter(
        (measure) => measure.category === category
      );
      categoryMeasures.forEach((measure) => {
        acc.push({
          ...measure,
          project,
          type: "implemented" as const,
          title: measure.description,
          description: "",
          date: measure.invoiceDate
            ? new Date(measure.invoiceDate).toISOString()
            : "",
          invoiceDate: measure.invoiceDate
            ? new Date(measure.invoiceDate)
            : undefined,
        });
      });
    }

    // Add planned measures
    if (planned && planned.length > 0) {
      const categoryMeasures = planned.filter(
        (measure) => measure.category === category
      );
      categoryMeasures.forEach((measure) => {
        acc.push({
          ...measure,
          project,
          type: "planned" as const,
          title: measure.measure,
          description: measure.description,
          date: measure.actionDate || "",
        });
      });
    }

    return acc;
  }, [] as UnifiedMeasure[]);

  const implementedMeasures = allMeasures.filter(
    (measure) => measure.type === "implemented"
  );
  const plannedMeasures = allMeasures.filter(
    (measure) => measure.type === "planned"
  );

  const displayedImplementedMeasures = showAllImplemented
    ? implementedMeasures
    : implementedMeasures.slice(0, 3);

  const displayedPlannedMeasures = showAllPlanned
    ? plannedMeasures
    : plannedMeasures.slice(0, 3);

  return (
    <div className="fixed inset-0 bg-[#FAFAFA] z-50 overflow-y-auto pb-6">
      {/* Hero Image */}
      <div className="relative">
        <div
          className="h-64 bg-cover bg-center"
          style={{
            backgroundImage: `url(${catImage})`,
          }}
        />
        {/* Back button on hero image */}
        <div className="absolute top-4 left-4 z-20">
          <Button
            variant="secondary"
            size="sm"
            onClick={onClose}
            className="bg-white"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
        </div>
        {/* Content */}
        <div className="bg-[#FAFAFA] rounded-t-3xl -mt-6 relative z-10 min-h-screen">
          <div className="bg-white rounded-t-3xl px-6 pt-8">
            {/* Category title and description */}
            <h2 className="text-2xl font-semibold mb-4 text-center">
              {catInfo?.displayName}
            </h2>
            <p className="text-gray-600 mb-6 text-center">
              {catInfo?.description}
            </p>
            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <div className="-mx-6">
                <TabsList className="w-full" paddingX="px-6">
                  <TabsTrigger value="implemented" activeColor={customerColor}>
                    {t("categoryDetail.implementedMeasures")}
                  </TabsTrigger>
                  <TabsTrigger value="planned" activeColor={customerColor}>
                    {t("categoryDetail.plannedMeasures")}
                  </TabsTrigger>
                </TabsList>
              </div>
            </Tabs>
          </div>

          {/* Tables (Tabs Content)*/}
          <div className="px-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsContent value="implemented" className="space-y-4">
                <p className="text-gray-500 text-sm py-6">
                  {t("categoryDetail.implementedMeasuresDescription")}
                </p>
                <div className="text-sm font-semibold text-gray-700">
                  {implementedMeasures.length}{" "}
                  {t("categoryDetail.implementedMeasures")}
                </div>

                {displayedImplementedMeasures.length > 0 ? (
                  <div className="space-y-4">
                    {displayedImplementedMeasures.map((measure, index) => (
                      <Card key={index} className="p-6">
                        <h3 className="font-semibold text-gray-900 mb-2">
                          {measure.title}
                        </h3>
                        <div className="flex items-center mb-3">
                          <p className="text-xs text-[#009982] font-semibold">
                            {getProjectTranslation(measure.project.nameTranslations, language) || measure.project.title}
                          </p>
                          <span className="text-gray-700 mx-1">•</span>
                          <span className="text-xs text-gray-700 font-semibold">
                            {formatDateLong(measure.date, language)}
                          </span>
                        </div>
                        {measure.description && (
                          <p className="text-gray-600 text-xs mb-4">
                            {measure.description}
                          </p>
                        )}
                      </Card>
                    ))}

                    {implementedMeasures.length > 3 && (
                      <div className="flex justify-center mt-12 mb-8">
                        <Button
                          onClick={() =>
                            setShowAllImplemented(!showAllImplemented)
                          }
                          style={{ backgroundColor: customerColor }}
                        >
                          {showAllImplemented
                            ? t("common.showLess")
                            : t("common.showMore")}
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    {t("categoryDetail.noImplementedMeasures")}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="planned" className="space-y-4">
                <p className="text-gray-500 text-sm py-6">
                  {t("categoryDetail.plannedMeasuresDescription")}
                </p>
                <div className="text-sm font-semibold text-gray-700">
                  {plannedMeasures.length} {t("categoryDetail.plannedMeasures")}
                </div>

                {displayedPlannedMeasures.length > 0 ? (
                  <div className="space-y-4">
                    {displayedPlannedMeasures.map((measure, index) => (
                      <Card key={index} className="p-6">
                        <h3 className="font-semibold text-gray-900 mb-2">
                          {measure.title}
                        </h3>
                        <div className="flex items-center mb-3">
                          <p className="text-xs text-[#009982] font-semibold">
                            {getProjectTranslation(measure.project.nameTranslations, language) || measure.project.title}
                          </p>
                          {measure.date && (
                            <>
                              <span className="text-gray-700 mx-1">•</span>
                              <span className="text-xs text-gray-700 font-semibold">
                                {formatDateLong(measure.date, language)}
                              </span>
                            </>
                          )}
                        </div>
                        {measure.description && (
                          <p className="text-gray-600 text-xs mb-4">
                            {measure.description}
                          </p>
                        )}
                      </Card>
                    ))}

                    {plannedMeasures.length > 3 && (
                      <div className="flex justify-center mt-12 mb-8">
                        <Button
                          onClick={() => setShowAllPlanned(!showAllPlanned)}
                          style={{ backgroundColor: customerColor }}
                        >
                          {showAllPlanned
                            ? t("common.showLess")
                            : t("common.showMore")}
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    {t("categoryDetail.noPlannedMeasures")}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryDetailMobilePage;
